import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type Document,
  deleteSubmissionDocument,
  fetchSubmissionDocuments,
  submissionDocumentsQueryKey,
  uploadSubmissionDocument,
} from '@/hooks/submissions/submissionDocumentApi';
import {
  type UploadStatus,
  useUploadProgress,
} from '@/hooks/submission_forms/useUploadProgress';

interface UseMultiUploadDocumentsOptions {
  submissionId: string | undefined;
  questionSlug: string;
  syncFilenames: (filenames: string[]) => void;
  onMutationSuccess?: () => void | Promise<void>;
}

export interface UploadProgress {
  done: number;
  total: number;
}

interface UseMultiUploadDocumentsReturn {
  files: Document[];
  isLoading: boolean;
  error: string | null;
  uploadFiles: (files: File[]) => Promise<void>;
  removeFile: (documentId: string) => Promise<void>;
  isUploading: boolean;
  uploadProgress: UploadProgress | null;
  /** Byte progress of the file currently being sent (OV-1380). */
  uploadStatus: UploadStatus;
  /** Documents stored by the latest batch — each gets an "Upload complete" mark. */
  recentlyUploadedIds: string[];
  removingId: string | null;
}

export function useMultiUploadDocuments({
  submissionId,
  questionSlug,
  syncFilenames,
  onMutationSuccess,
}: UseMultiUploadDocumentsOptions): UseMultiUploadDocumentsReturn {
  const queryClient = useQueryClient();
  const [mutationError, setMutationError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress | null>(
    null
  );
  const [recentlyUploadedIds, setRecentlyUploadedIds] = useState<string[]>([]);
  const { status: fileStatus, track } = useUploadProgress();

  const {
    data: documents = [],
    isLoading,
    error: queryError,
  } = useQuery({
    queryKey: submissionDocumentsQueryKey(submissionId ?? ''),
    queryFn: () => fetchSubmissionDocuments(submissionId!),
    enabled: !!submissionId,
  });

  const files = useMemo(
    () => documents.filter(doc => doc.question_slug === questionSlug),
    [documents, questionSlug]
  );

  useEffect(() => {
    syncFilenames(files.map(f => f.filename));
  }, [files, syncFilenames]);

  const invalidateDocuments = async () => {
    if (!submissionId) return;
    await queryClient.invalidateQueries({
      queryKey: submissionDocumentsQueryKey(submissionId),
    });
  };

  // Files go up one request at a time, never in parallel: SaveUploadService
  // creates the SubmissionAnswer on the first upload and enforces duplicate
  // names and the per-answer file cap per request, so concurrent requests
  // could race to create two answers or overshoot the cap. A failed file does
  // not stop the rest of the batch; each failure is reported by filename.
  const uploadMutation = useMutation({
    mutationFn: async (batch: File[]) => {
      const failures: string[] = [];
      let uploaded = 0;
      setUploadProgress({ done: 0, total: batch.length });

      for (const file of batch) {
        try {
          const document = await track(file.name, onProgress =>
            uploadSubmissionDocument({
              submissionId: submissionId!,
              questionSlug,
              file,
              onProgress,
            })
          );
          uploaded += 1;
          if (document?.id) {
            setRecentlyUploadedIds(ids => [...ids, document.id]);
          }
        } catch (err) {
          const message =
            err instanceof Error ? err.message : 'Failed to upload file';
          failures.push(`${file.name}: ${message}`);
        }
        setUploadProgress({
          done: uploaded + failures.length,
          total: batch.length,
        });
      }

      return { uploaded, failures };
    },
    onMutate: () => {
      setMutationError(null);
      setRecentlyUploadedIds([]);
    },
    onSuccess: async ({ uploaded, failures }) => {
      if (failures.length > 0) setMutationError(failures.join('; '));
      if (uploaded > 0) {
        await invalidateDocuments();
        await onMutationSuccess?.();
      }
    },
    onError: (err: Error) => setMutationError(err.message),
    onSettled: () => setUploadProgress(null),
  });

  const deleteMutation = useMutation({
    mutationFn: (documentId: string) =>
      deleteSubmissionDocument({
        submissionId: submissionId!,
        documentId,
      }),
    onMutate: () => setMutationError(null),
    onSuccess: async () => {
      await invalidateDocuments();
      await onMutationSuccess?.();
    },
    onError: (err: Error) => setMutationError(err.message),
  });

  const uploadFiles = async (batch: File[]) => {
    if (!submissionId || batch.length === 0) return;
    await uploadMutation.mutateAsync(batch);
  };

  const removeFile = async (documentId: string) => {
    if (!submissionId) return;
    await deleteMutation.mutateAsync(documentId);
  };

  const error =
    mutationError ?? (queryError instanceof Error ? queryError.message : null);

  // The last file is stored before the list is refetched (onSuccess). Keep
  // showing "finishing" until the batch settles, so the indicator never goes
  // blank before the new rows — and their "Upload complete" marks — appear.
  const uploadStatus: UploadStatus =
    uploadMutation.isPending && fileStatus?.phase === 'complete'
      ? { phase: 'finishing', fileName: fileStatus.fileName }
      : fileStatus;

  return {
    files,
    isLoading,
    error,
    uploadFiles,
    removeFile,
    isUploading: uploadMutation.isPending,
    uploadProgress,
    uploadStatus,
    recentlyUploadedIds,
    removingId: deleteMutation.isPending
      ? (deleteMutation.variables ?? null)
      : null,
  };
}
